/**
 * A refusal, as lines a person can act on.
 *
 * The API answers a refused request with `{ error, messageKey, details: [{ path, code }] }`: the
 * sentence says what kind of refusal it was, and each detail says which field and why. A screen
 * that shows only the sentence ("The request was not valid.") leaves the person guessing which of
 * a dozen fields to change — and an Ops cancel before pickup failed that way for a whole release
 * without anybody being told why.
 *
 * So: the API's sentence, then one line per detail, the code looked up through the screen's own
 * namespaces, then the shared `config.issue.*` vocabulary, then — never blank — the raw code. The
 * shape of `peopleRefusal` (`pages/ops-people.tsx`) and `refusalLines`
 * (`pages/ops-config-policy.tsx`), which keep theirs; this is the one the other screens share.
 *
 * A few paths are not fields and say more read another way: the Ops facet a route needs, the
 * justification header every cross-tenant read carries, the trigger an action named, a refused
 * transition's current state and lawful alternative, and why a screening could not run.
 */
import type { useTranslation } from 'react-i18next';
import { ApiRequestError } from '../api/client.js';

type Translate = ReturnType<typeof useTranslation>['t'];

export interface RefusalOptions {
  /** Where a detail's code is looked up first, in order; `config.issue` and the raw code follow. */
  readonly namespaces?: readonly string[];
  /** The label a screen shows for a field path; the raw path otherwise. */
  readonly label?: (path: string) => string;
  /**
   * Who is reading. A refused transition's lawful alternative is an Ops action, which an operator
   * can take and a Merchant cannot — so the Merchant is told it can no longer be done here.
   */
  readonly audience?: 'ops' | 'merchant';
}

/**
 * The one lawful alternative the state machine names, by the event it cites, mapped to the Ops
 * action that performs it (`machines/shipment.ts` `forbidden`: a cancel after pickup is refused,
 * "use rto.initiated instead").
 */
const INSTEAD_ACTION: Readonly<Record<string, string>> = { 'rto.initiated': 'rto.initiate' };

/**
 * Why a screening could not run, as one sentence of its own (ADR-0071 §8, amended 2026-09-28). A
 * refused onboarding or rescreen carries each fail-closed reason as `{ path: 'screening', code }`;
 * the path is not a field anybody can fix, so the line is the reason alone, never `screening: …`.
 */
export function screeningUnavailableLine(t: Translate, code: string): string {
  return t(`screening.unavailable.${code}`, code);
}

/** `t(first, t(second, … raw))`: the first namespace that has the code wins. */
function lookup(t: Translate, code: string, namespaces: readonly string[]): string {
  return [...namespaces, 'config.issue'].reduceRight<string>((fallback, ns) => t(`${ns}.${code}`, fallback), code);
}

function detailLine(
  t: Translate,
  error: string,
  detail: { readonly path: string; readonly code: string },
  options: RefusalOptions,
): string | undefined {
  if (error === 'transition_refused') {
    if (detail.path === 'instead') {
      const action = INSTEAD_ACTION[detail.code.split(/[\s(,]/u)[0] ?? ''];
      if (options.audience === 'merchant') return t('refusal.merchantInstead');
      return action ? t('refusal.instead', { action: t(`ops.action.${action}`, action) }) : undefined;
    }
    // The path is the state the record is in now; the code cites the table row, which is for us.
    return t('refusal.nowIn', { state: t(`orderStatus.${detail.path}`, detail.path) });
  }
  if (detail.path === 'screening') return screeningUnavailableLine(t, detail.code);
  if (detail.path === 'facet') return t('refusal.needsFacet', { facet: t(`ops.facet.${detail.code}`, detail.code) });
  if (detail.path === 'trigger') return t('refusal.trigger', { action: t(`ops.action.${detail.code}`, detail.code) });
  const where = detail.path === 'X-Tard-Justification' ? t('refusal.justification') : (options.label?.(detail.path) ?? detail.path);
  return `${where}: ${lookup(t, detail.code, options.namespaces ?? [])}`;
}

/** The API's sentence, then one line per detail. Anything that is not an API refusal is "unexpected". */
export function refusalLines(t: Translate, caught: unknown, options: RefusalOptions = {}): readonly string[] {
  if (!(caught instanceof ApiRequestError)) return [t('error.unexpected')];
  const lines: string[] = [t(caught.body.messageKey)];
  for (const detail of caught.body.details ?? []) {
    const line = detailLine(t, caught.body.error, detail, options);
    if (line !== undefined && !lines.includes(line)) lines.push(line);
  }
  return lines;
}
